<template>
  <div class="cloudflare-image-preview" :class="{ 'fullscreen': isFullscreen }">
    <div class="media-container">
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
      </div>

      <!-- Error message -->
      <div v-if="hasError" class="error-message">
        <span>{{ errorMessage || 'Failed to load image' }}</span>
      </div>

      <!-- Image with responsive sizing -->
      <img 
        v-if="!hasError" 
        class="image-element"
        :src="optimizedImageUrl" 
        :alt="alt" 
        @error="handleMediaError" 
        @load="handleMediaLoaded"
        @click="toggleFullscreen"
      />

      <!-- Fullscreen button -->
      <div v-if="!isFullscreen && !isLoading && !hasError" class="image-controls">
        <button class="fullscreen-btn" @click.stop="toggleFullscreen" title="Toggle fullscreen">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
          </svg>
        </button>
      </div>
      
      <!-- Close button for fullscreen mode -->
      <button v-if="isFullscreen" class="close-btn" @click.stop="closeFullscreen" title="Exit fullscreen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- Image metadata (optional) -->
      <div v-if="showMetadata && imageMetadata" class="image-metadata">
        <p v-if="imageMetadata.width && imageMetadata.height">Dimensions: {{ imageMetadata.width }}x{{ imageMetadata.height }}</p>
        <p v-if="imageMetadata.size">Size: {{ formatFileSize(imageMetadata.size) }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'CloudflareImagePreview',
  props: {
    src: {
      type: String,
      required: true,
      validator: (value) => {
        // Basic URL validation
        return /^https?:\/\/.+/.test(value);
      }
    },
    alt: {
      type: String,
      default: 'Image preview'
    },
    width: {
      type: [Number, String],
      default: null
    },
    height: {
      type: [Number, String],
      default: null
    },
    fit: {
      type: String,
      default: 'scale-down', // 'scale-down', 'contain', 'cover', 'crop', 'pad'
      validator: (value) => {
        return ['scale-down', 'contain', 'cover', 'crop', 'pad'].includes(value);
      }
    },
    quality: {
      type: [Number, String],
      default: 85,
      validator: (value) => {
        const num = parseInt(value, 10);
        return num >= 1 && num <= 100;
      }
    },
    showMetadata: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const isLoading = ref(true);
    const hasError = ref(false);
    const errorMessage = ref('');
    const isFullscreen = ref(false);
    const imageMetadata = ref(null);
    
    // Generate optimized image URL with Cloudflare parameters
    const optimizedImageUrl = computed(() => {
      if (!props.src) return '';
      
      // Check if this is already a Cloudflare R2 URL
      const isCloudflareUrl = props.src.includes('.r2.cloudflarestorage.com');
      
      // If not a Cloudflare URL, return original URL
      if (!isCloudflareUrl) return props.src;
      
      // Create URL with parameters for Cloudflare
      try {
        const url = new URL(props.src);
        const params = new URLSearchParams();
        
        if (props.width) params.append('width', props.width);
        if (props.height) params.append('height', props.height);
        if (props.fit) params.append('fit', props.fit);
        if (props.quality) params.append('quality', props.quality);
        
        // If format isn't specified and we want WebP, add it
        params.append('format', 'auto');
        
        // Add the parameters to URL
        const paramString = params.toString();
        if (paramString) {
          url.search = url.search ? `${url.search}&${paramString}` : `?${paramString}`;
        }
        
        return url.toString();
      } catch (e) {
        console.error('Error generating optimized URL:', e);
        return props.src;
      }
    });
    
    // Handle image loaded event
    const handleMediaLoaded = (event) => {
      isLoading.value = false;
      hasError.value = false;
      
      // Extract image metadata
      if (event.target) {
        imageMetadata.value = {
          width: event.target.naturalWidth,
          height: event.target.naturalHeight,
        };
        
        emit('loaded', {
          width: event.target.naturalWidth,
          height: event.target.naturalHeight,
          src: props.src,
          element: event.target
        });
      }
    };
    
    // Handle media error events
    const handleMediaError = (event) => {
      isLoading.value = false;
      hasError.value = true;
      errorMessage.value = `Failed to load image: ${event.type}`;
      emit('error', { message: errorMessage.value, src: props.src });
    };
    
    // Toggle fullscreen mode
    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value;
      
      // Add/remove body class to prevent scrolling
      if (isFullscreen.value) {
        document.body.classList.add('image-viewer-open');
      } else {
        document.body.classList.remove('image-viewer-open');
      }
      
      emit('fullscreen-change', isFullscreen.value);
    };
    
    // Close fullscreen mode
    const closeFullscreen = () => {
      isFullscreen.value = false;
      document.body.classList.remove('image-viewer-open');
      emit('fullscreen-change', false);
    };
    
    // Format file size for display
    const formatFileSize = (bytes) => {
      if (!bytes) return 'Unknown';
      
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      if (bytes === 0) return '0 Bytes';
      
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
      if (i === 0) return `${bytes} ${sizes[i]}`;
      
      return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
    };
    
    // Handle escape key for exiting fullscreen
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFullscreen.value) {
        closeFullscreen();
      }
    };
    
    // Add event listeners on mount
    onMounted(() => {
      window.addEventListener('keydown', handleKeyDown);
    });
    
    // Remove event listeners before unmount
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('image-viewer-open');
    });
    
    return {
      isLoading,
      hasError,
      errorMessage,
      isFullscreen,
      imageMetadata,
      optimizedImageUrl,
      handleMediaLoaded,
      handleMediaError,
      toggleFullscreen,
      closeFullscreen,
      formatFileSize
    };
  }
};
</script>

<style scoped>
.cloudflare-image-preview {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f3f4f6;
}

.cloudflare-image-preview.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  border-radius: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.image-element {
  max-width: 100%;
  min-height: 150px;
  max-height: 300px;
  object-fit: contain;
  display: block;
  transition: transform 0.3s ease;
  cursor: pointer;
  border-radius: 4px;
}

.fullscreen .image-element {
  max-height: 90vh;
  max-width: 90vw;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  border-radius: 0;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-message {
  color: #ef4444;
  padding: 10px;
  text-align: center;
  border: 1px solid #ef4444;
  border-radius: 4px;
  background-color: #fef2f2;
  width: 100%;
}

.image-controls {
  position: absolute;
  bottom: 20px;
  right: 20px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.cloudflare-image-preview:hover .image-controls {
  opacity: 1;
}

.fullscreen-btn,
.close-btn {
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: background-color 0.2s ease;
}

.fullscreen-btn:hover,
.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
}

.image-metadata {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  width: 100%;
  text-align: center;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 